<template>
  <form class="p-5 bg-gray-100 rounded m-2" @submit.prevent>
    <p class="font-bold text-center w-full" v-text="i18n.login.title"></p>

    <!-- email input -->
    <div class="py-1" v-if="mode !== 'register' && !reset_message">
      <label for="email" v-text="i18n.login.email"></label>
      <input
        id="email"
        v-model="form.email"
        :class="{
          danger: form.errors.has('email'),
          'cursor-not-allowed': isEmailInputDisable,
        }"
        :disabled="isEmailInputDisable"
      />
      <span class="help danger">
        <span
          v-if="form.errors.has('email')"
          v-text="form.errors.get('email')"
        ></span>
        <!-- TODO add i18n -->
        <span v-if="!isEmailExistOnSigningMethod">{{
          i18n.login.skip || "New user? Create your account"
        }}</span>
      </span>
    </div>
    <div class="py-6" v-if="reset_message">
      <div
        class="p-3 border rounded border-green-200 bg-green-100 text-green-800"
      >
        <font-awesome-icon icon="check" class="mx-6"></font-awesome-icon>
        <span v-text="reset_message"></span>
      </div>
    </div>

    <!-- password input only on step 2 -->
    <div
      class="py-1"
      v-if="mode === 'login' && login_step === 'login_password_method'"
    >
      <label for="password" v-text="i18n.login.password"></label>
      <input
        id="password"
        type="password"
        v-model="form.password"
        :class="{ danger: form.errors.has('password') }"
      />
      <span class="help danger">
        <span
          v-if="form.errors.has('password')"
          v-text="form.errors.get('password')"
        ></span>
      </span>
    </div>

    <!-- captcha input only on step 1 -->
    <div class="py-1">
      <CaptchaComponent
        v-if="mode === 'login' && login_step == 'signing_methods'"
        @tokenReceived="handleCaptchaToken"
        ref="captchaComponent"
      />
    </div>

    <!-- toggle button - switch register/login -->
    <div class="flex justify-center py-3" v-if="mode === 'register'">
      <button
        type="submit"
        class="button button-base action"
        v-text="i18n.login.go"
        @click="toggle"
      ></button>
    </div>

    <!-- login -->
    <div
      class="flex justify-between py-3"
      v-if="mode === 'reset' || mode === 'login'"
    >
      <!--forgotten button on login form -->
      <button
        type="button"
        class="button button-base action-secondary"
        v-text="i18n.login.forgotten"
        @click="toggle"
        v-if="mode === 'login'"
      ></button>

      <!-- submit button on login form -->
      <button
        type="submit"
        :class="[
          'button button-base action',
          {
            danger: form.errors.has('email'),
            'cursor-not-allowed': isSubmitInputDisable,
          },
        ]"
        v-text="i18n.login.go"
        v-if="mode === 'login'"
        @click="login"
        :disabled="isSubmitInputDisable"
      ></button>

      <!-- return button on forgotten form -->
      <button
        type="button"
        class="button button-base action-secondary"
        v-if="mode === 'reset'"
        @click="toggle"
      >
        <font-awesome-icon icon="chevron-left" class="mr-1"></font-awesome-icon>
        {{ i18n.login.go }}
      </button>

      <!-- submit button on forgotten form -->
      <button
        type="submit"
        class="button button-base action"
        :class="resetting ? 'cursor-not-allowed' : ''"
        v-if="mode === 'reset' && !reset_message"
        @click="sendResetPassword"
      >
        <span v-text="i18n.reset.go" v-if="!resetting"></span>
        <font-awesome-icon
          :icon="['fas', 'cog']"
          spin
          v-if="resetting"
        ></font-awesome-icon>
      </button>
    </div>
  </form>
</template>

<script>
import { Form } from "../Utilities/Form";
import CaptchaComponent from "./CaptchaComponent.vue";

export default {
  props: ["i18n", "loginRoute", "resetRoute", "signingMethodsRoute"],
  components: { CaptchaComponent },
  mixins: [],
  data: function () {
    return {
      // Represents the current mode of the component (login, register, reset).
      mode: "login",

      // A flag to determine if the password reset process is in progress.
      resetting: false,

      // Message to show after attempting to reset the password.
      reset_message: "",

      // Represents the current step in the login process.
      // "signing_methods" indicates the email verification step.
      // "login_password_method" indicates the password login step.
      login_step: "signing_methods",

      // Represents the form and its fields.
      form: new Form({
        email: "",
        password: "",
        // Captcha verification token.
        turnstile: "",
      }),

      // Indicates if the email exists for the signing method.
      // Set to true by default, and can be modified based on server response.
      isEmailExistOnSigningMethod: true,

      // Flags to determine if respective input fields or buttons should be disabled.
      isEmailInputDisable: false,
      isSubmitInputDisable: true,
    };
  },
  methods: {
    /**
     * Triggers the re-render of the captchaComponent by calling its resetComponent method.
     * This method is used when the parent component needs to force a re-render or reset
     * of the captchaComponent, typically in response to specific events or data changes
     * in the parent component.
     */
    refreshTurnstile() {
      this.$refs.captchaComponent.resetChild(); // Invoking resetComponent on the child component
    },

    /**
     * Handles the captcha token retrieved from the CaptchaComponent.
     * @param {string} token - The captcha token.
     */
    handleCaptchaToken(token) {
      this.form.turnstile = token;
      this.isSubmitInputDisable = false;
    },

    /**
     * Resets the form and associated messages.
     */
    init() {
      this.form.reset();
      this.reset_message = "";
    },

    /**
     * Toggles between login, register, and reset modes.
     */
    toggle() {
      if (this.mode === "register") {
        this.$emit("mode-register");
        this.mode = "login";
      } else if (this.mode === "reset") {
        this.mode = "login";
      } else {
        this.mode = "reset";
      }
      this.init();
    },

    /**
     * Sends a password reset request.
     */
    sendResetPassword() {
      if (!this.resetting) {
        this.form.reset();
        this.resetting = true;
        this.form
          .post(this.resetRoute)
          .then(this.handlePasswordResetResponse)
          .catch(this.handlePasswordResetError);
      }
    },

    /**
     * Handles the response after a password reset request.
     * @param {Object} data - The response data.
     */
    handlePasswordResetResponse(data) {
      if (window.google_analytics_pageview) {
        window.google_analytics_pageview("/form-submit/login/password-reseted");
      }
      this.init();
      this.reset_message = data.success;
      this.resetting = false;
    },

    /**
     * Handles errors during the password reset process.
     */
    handlePasswordResetError() {
      this.resetting = false;
    },

    /**
     * Processes the login depending on the current login step.
     */
    login() {
      if (this.login_step === "signing_methods") {
        this.handleSigningMethodsLogin();
      } else if (this.login_step === "login_password_method") {
        this.handlePasswordLogin();
      }
    },

    /**
     * Handles the signing methods login process.
     */
    handleSigningMethodsLogin() {
      this.recordAnalyticsPageView("/form-submit/login/signing-methods");
      this.form.reset();
      this.form
        .post(this.signingMethodsRoute)
        .then(this.handleSigningMethodsResponse)
        .catch(this.handleLoginError);
    },

    /**
     * Handles the response from the signing methods login process.
     * @param {Object} data - The response data.
     */
    handleSigningMethodsResponse(data) {
      if (!data.success) return;

      this.isEmailExistOnSigningMethod =
        data["login-type"] !== "Register-first";

      switch (data["login-type"]) {
        case "Register-first":
          this.login_step = "signing_methods";
          this.isEmailInputDisable = false;
          console.log("Register-first");
          this.refreshTurnstile();
          break;
        case "Login-with-password":
          this.login_step = "login_password_method";
          this.isEmailInputDisable = true;
          console.log("login_step");
          break;
        case "SSO":
          // Handle login with SSO here if needed
          break;
        default:
          // Default action if needed
          this.refreshTurnstile();
          break;
      }
    },

    /**
     * Handles the standard password login process.
     */
    handlePasswordLogin() {
      this.recordAnalyticsPageView("/form-submit/login/login");
      this.form.reset();
      this.form
        .post(this.loginRoute)
        .then(this.handlePasswordLoginResponse)
        .catch(this.handleLoginError);
    },

    /**
     * Handles the response from the standard password login process.
     * @param {Object} data - The response data.
     */
    handlePasswordLoginResponse(data) {
      this.init();
      if (data.success === "admin") {
        window.location.href = "/admin/stats/monthly";
      } else {
        this.recordAnalyticsPageView("/form-submit/login/logged-in");
        this.$emit("logged-in");
      }
    },

    /**
     * Records a page view in Google Analytics.
     * @param {string} path - The path to record.
     */
    recordAnalyticsPageView(path) {
      if (window.google_analytics_pageview) {
        window.google_analytics_pageview(path);
      }
    },

    /**
     * Handles errors during the login process.
     */
    handleLoginError() {
      this.refreshTurnstile();
      this.running = false;
    },
  },
};
</script>

<style></style>
